import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { 
  Building2, 
  Calendar, 
  Clock, 
  ArrowLeft,
  Receipt,
  CheckCircle,
  AlertTriangle,
  FileText,
  Zap,
  Shield,
  Building
} from 'lucide-react';

export default function FacturationElectroniqueArticle() {
  return (
    <>
      <SEO 
        title="Facturation Électronique Obligatoire 2026 | Marchés Publics"
        description="La facturation électronique devient obligatoire pour les marchés publics. Préparez-vous aux nouvelles obligations 2026."
        path="/blog/facturation-electronique-marches-publics-2026"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                Conformité
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                NOUVEAU
              </span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Facturation Électronique : Obligations Septembre 2026 pour les Marchés Publics
            </h1>
            <div className="flex items-center gap-6 text-slate-500">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                13 février 2026
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                9 min de lecture
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-12 mb-12 text-white text-center">
            <Receipt className="w-20 h-20 mx-auto mb-4 opacity-90" />
            <p className="text-xl font-medium">Obligation e-facturation</p>
            <p className="text-amber-100">1er septembre 2026 - Calendrier et mise en conformité</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Le <strong>1er septembre 2026</strong> marque une étape majeure pour les titulaires de marchés publics : 
              l'obligation de <strong>facturation électronique</strong> entre en vigueur. Toutes les entreprises 
              devront être capables de recevoir des factures électroniques, et les grandes entreprises/ETI devront 
              en émettre. Guide complet pour être prêt à temps.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-amber-900 mb-2">Impact Fort - 8/10</h4>
                  <p className="text-amber-800">
                    Les titulaires de marchés non conformes s'exposeront à des <strong>rejets de paiement</strong>. 
                    La mise en conformité est urgente pour toutes les entreprises travaillant avec le secteur public.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Calendar className="w-7 h-7 text-amber-600" />
              Calendrier d'entrée en vigueur
            </h2>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 my-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4 pb-6 border-b border-amber-200">
                  <div className="w-32 text-sm font-bold text-amber-700 flex-shrink-0">1er sept. 2026</div>
                  <div>
                    <h4 className="font-bold text-slate-900">Grandes entreprises & ETI</h4>
                    <ul className="text-slate-600 text-sm mt-2 space-y-1">
                      <li>• Obligation d'<strong>émettre</strong> des factures électroniques</li>
                      <li>• Obligation de <strong>recevoir</strong> des factures électroniques</li>
                      <li>• Transmission via plateforme de dématérialisation partenaire (PDP)</li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-start gap-4 pb-6 border-b border-amber-200">
                  <div className="w-32 text-sm font-bold text-amber-700 flex-shrink-0">1er sept. 2026</div>
                  <div>
                    <h4 className="font-bold text-slate-900">PME & TPE</h4>
                    <ul className="text-slate-600 text-sm mt-2 space-y-1">
                      <li>• Obligation de <strong>recevoir</strong> des factures électroniques</li>
                      <li>• Pas encore d'obligation d'émission</li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-32 text-sm font-bold text-amber-700 flex-shrink-0">1er sept. 2027</div>
                  <div>
                    <h4 className="font-bold text-slate-900">PME & Micro-entreprises</h4>
                    <ul className="text-slate-600 text-sm mt-2 space-y-1">
                      <li>• Obligation d'<strong>émettre</strong> des factures électroniques</li>
                      <li>• Fin du régime transitoire</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Building className="w-7 h-7 text-amber-600" />
              Qui est concerné par catégorie d'entreprise ?
            </h2>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border-2 border-amber-200 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-4">Grandes entreprises</h4>
                <p className="text-sm text-slate-500 mb-4">CA &gt; 1,5 Mrd € ou effectif &gt; 5000 salariés</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Émission obligatoire sept. 2026</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Réception obligatoire sept. 2026</span>
                  </div>
                </div>
              </div>
              <div className="bg-white border-2 border-amber-200 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-4">ETI (Entreprises de Taille Intermédiaire)</h4>
                <p className="text-sm text-slate-500 mb-4">250 à 5000 salariés ou CA &lt; 1,5 Mrd €</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Émission obligatoire sept. 2026</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Réception obligatoire sept. 2026</span>
                  </div>
                </div>
              </div>
              <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-4">PME</h4>
                <p className="text-sm text-slate-500 mb-4">10 à 249 salariés ou CA &lt; 50 M€</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Émission obligatoire sept. 2027</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Réception obligatoire sept. 2026</span>
                  </div>
                </div>
              </div>
              <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-4">TPE / Micro-entreprises</h4>
                <p className="text-sm text-slate-500 mb-4">&lt; 10 salariés ou CA &lt; 2 M€</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Émission obligatoire sept. 2027</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Réception obligatoire sept. 2026</span>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 text-amber-600" />
              Impact spécifique pour les marchés publics
            </h2>

            <p>
              Pour les entreprises titulaires de marchés publics, la facturation électronique présente 
              des <strong>enjeux particuliers</strong> :
            </p>

            <div className="space-y-4 my-8">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-slate-900">Chorus Pro reste obligatoire</h4>
                  <p className="text-slate-600">Pour les marchés de l'État et des collectivités, la facturation via <strong>Chorus Pro</strong> reste la norme. La e-facturation s'ajoute aux obligations existantes.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <Zap className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-slate-900">Sous-traitance concernée</h4>
                  <p className="text-slate-600">Les sous-traitants déclarés devront également émettre des factures électroniques pour les paiements directs.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-red-900">Risque de rejet de paiement</h4>
                  <p className="text-red-800">Une facture non conforme au format électronique pourra être rejetée, retardant significativement le paiement.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Comment se mettre en conformité ?
            </h2>

            <div className="space-y-4 my-8">
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-bold text-amber-900">Choisissez une plateforme de dématérialisation (PDP)</h4>
                  <p className="text-amber-800">Sélectionnez une PDP immatriculée par l'administration fiscale pour émettre et recevoir vos factures.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-bold text-amber-900">Mettez à jour vos systèmes</h4>
                  <p className="text-amber-800">Assurez-vous que votre logiciel de facturation peut générer des factures au format Factur-X ou UBL.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-bold text-amber-900">Formez vos équipes</h4>
                  <p className="text-amber-800">Sensibilisez vos collaborateurs aux nouvelles procédures de facturation et de suivi.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-bold text-amber-900">Testez avant l'échéance</h4>
                  <p className="text-amber-800">Effectuez des tests d'émission et de réception avant le 1er septembre pour anticiper les problèmes.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Les formats de facture électronique acceptés
            </h2>

            <div className="bg-slate-100 rounded-xl p-6 my-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 text-slate-900">Format</th>
                    <th className="text-left py-3 text-slate-900">Description</th>
                    <th className="text-left py-3 text-slate-900">Usage</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-b border-slate-200">
                    <td className="py-3 font-medium">Factur-X</td>
                    <td className="py-3">PDF avec données XML intégrées</td>
                    <td className="py-3">Recommandé pour les PME</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 font-medium">UBL</td>
                    <td className="py-3">Format XML structuré</td>
                    <td className="py-3">Standard européen</td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium">CII</td>
                    <td className="py-3">Cross Industry Invoice</td>
                    <td className="py-3">Standard UN/CEFACT</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 my-8">
              <h4 className="font-bold text-violet-900 mb-3">Bon à savoir</h4>
              <p className="text-violet-800">
                Le format <strong>Factur-X</strong> est particulièrement adapté aux PME : il combine un PDF 
                lisible par l'humain et des données XML exploitables par les systèmes automatisés. 
                C'est le format recommandé pour commencer.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Conclusion : anticiper pour éviter les blocages
            </h2>

            <p>
              La facturation électronique représente un changement majeur pour les titulaires de marchés publics. 
              Ne pas être prêt au <strong>1er septembre 2026</strong> expose à des <strong>retards de paiement</strong> 
              potentiellement critiques pour la trésorerie.
            </p>

            <p>
              Anticipez dès maintenant : choisissez votre plateforme, formez vos équipes et testez vos processus. 
              La mise en conformité est un investissement qui vous évitera bien des problèmes.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">
              Trouvez les marchés adaptés à votre entreprise
            </h3>
            <p className="text-violet-100 mb-6">
              Pendant que vous préparez votre mise en conformité, ne manquez pas les opportunités de marchés publics.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-700 hover:bg-slate-100">
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
